<template>
    <div>
        <h3>{{title}}</h3>
        <div class="row">
            <div class="form-group col-md-4">
                <label class="font-weight-bold required" :for="'country' + _uid">{{ $t('address.country') }}</label>
                <dropdown-search ref="countryDropdown"
                                 :loadByParentSort="true"
                                 :parentSort="contriesSort"
                                 :id="'country' + _uid"
                                 v-model="address.countryId"
                                 v-validate="'required'"
                                 :name="'country' + _uid"
                                 :data-vv-as="$t('address.country')"
                                 :disabled="isAddressDetails && !editing"></dropdown-search>
                <span class="text-danger"
                      v-if="errors.has('country' + _uid)">{{ errors.first('country' + _uid) }}</span>
            </div>
            <div class="form-group col-md-8">
                <label class="font-weight-bold required" :for="'settlement' + _uid">
                    {{
                    $t('address.settlement')
                    }}
                </label>
                <div v-if="address.countryId == countries.BG">
                    <city-dropdown-search ref="settlementDropdown"
                                          :id="'settlement' + _uid"
                                          v-model="address.settlementId"
                                          v-validate="address.countryId == countries.BG ? 'required' : ''"
                                          :name="'settlementId' + _uid"
                                          @input="cityChanged"
                                          :data-vv-as="$t('address.settlement')"
                                          :disabled="isAddressDetails && !editing"
                                          aria-describedby="SettlementIdHelper"></city-dropdown-search>
                    <small id="SettlementIdHelper"
                           class="form-text text-muted">{{$t('address.settlementIdHelper')}}</small>
                    <span class="text-danger" v-if="errors.has('settlementId' + _uid)">{{ errors.first('settlementId' + _uid) }}</span>
                </div>
                <div v-if="address.countryId != countries.BG">
                    <input type="text"
                           class="form-control"
                           :id="'settlement' + _uid"
                           v-model="address.settlement"
                           v-validate="address.countryId != countries.BG ? 'required' : ''"
                           :name="'settlement' + _uid"
                           :data-vv-as="$t('address.settlement')"
                           :readonly="isAddressDetails && !editing"
                           aria-describedby="SettlementHelper"/>
                    <small id="SettlementHelper" class="form-text text-muted">{{$t('address.settlementHelper')}}</small>
                    <span class="text-danger" v-if="errors.has('settlement' + _uid)">{{ errors.first('settlement' + _uid) }}</span>
                </div>
            </div>
            <div class="form-group col-md-4">
                <label class="font-weight-bold" :for="'postCode' + _uid">{{$t('address.postCode') }}</label>
                <input type="text" class="form-control" v-model="address.postCode" :id="'portCode' + _uid"
                       :readonly="isAddressDetails && !editing"/>
            </div>
            <div class="form-group col-md-4">
                <label class="font-weight-bold" :class="required?'required':''" :for="'street' + _uid">{{
                    $t('address.street') }}</label>
                <input type="text"
                       class="form-control"
                       v-model="address.street"
                       v-validate="required?'required':''"
                       :name="'street' + _uid"
                       :data-vv-as="$t('address.street')"
                       :id="'street' + _uid"
                       :readonly="isAddressDetails && !editing"/>
                <span class="text-danger" v-if="errors.has('street' + _uid)">{{ errors.first('street' + _uid) }}</span>
            </div>
            <div class="form-group col-md-4">
                <label class="font-weight-bold" :class="required?'required':''" :for="'streetNum' + _uid">
                    {{
                    $t('address.streetNum')
                    }}
                </label>
                <input type="text"
                       class="form-control"
                       v-model="address.num"
                       v-validate="required ? 'required' : ''"
                       :name="'streetNum' + _uid"
                       :data-vv-as="$t('address.streetNum')"
                       :id="'streetNum' + _uid"
                       :readonly="isAddressDetails && !editing"/>
                <span class="text-danger"
                      v-if="errors.has('streetNum' + _uid)">{{ errors.first('streetNum' + _uid) }}</span>
            </div>

            <div class="form-group col-md-12" v-if="showAreaOfAppeal">
                <label class="font-weight-bold" :for="'areaOfAppeal' + _uid">
                    {{
                    $t('address.areaOfAppeal')
                    }}
                </label>
                <input type="text"
                       class="form-control"
                       v-model="areaOfAppeal"
                       :name="'areaOfAppeal' + _uid"
                       :data-vv-as="$t('address.streetNum')"
                       :id="'areaOfAppeal' + _uid"
                       :readonly="true"/>
            </div>

            <div class="form-group col-md-12">
                <label class="font-weight-bold" :for="'coords' + _uid">{{ $t('address.coords') }}</label>

                <div class="form-row" :id="'coords' + _uid">
                    <div class="form-group col-md-4">
                        <div class="form-group row">
                            <label class="col-sm-3 col-form-label" :for="'lat' + _uid">
                                {{
                                $t('address.lat')
                                }}
                            </label>
                            <div class="col-sm-9">
                                <input type="number" class="form-control" v-model="address.lat" :id="'lat' + _uid"
                                       :readonly="isAddressDetails && !editing"/>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-md-4">
                        <div class="form-group row">
                            <label class="col-sm-3 col-form-label" :for="'long' + _uid">
                                {{
                                $t('address.long')
                                }}
                            </label>
                            <div class="col-sm-9">
                                <input type="number" class="form-control" v-model="address.long" :id="'long' + _uid"
                                       :readonly="isAddressDetails && !editing"/>
                            </div>
                        </div>
                    </div>

                    <div class="form-group col-md-4">
                        <div class="loader" v-if="coordsLoading || addressLoading"></div>
                        <div v-else class="btn-group" role="group" aria-label="address-btns">
                            <button type="button" class="btn btn-sm btn-secondary" @click.stop="getCoords"
                                    :disabled="isAddressDetails && !editing">{{ $t('address.coordsFromAddress') }}
                            </button>
                            <!--<button type="button" class="btn btn-sm btn-secondary"
                                    @click.stop="getAddress">
                                Адрес от координати
                            </button>-->
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-group col-12" v-if="allowEdit">
                <button v-if="!isAddressDetails" type="button" class="btn btn-primary btn"
                        :class="saving ? 'disabled' : ''" @click.stop="createOrEdit">
                    <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                    <i class="fas fa-save" v-else></i>
                    {{ isNew ? $t('buttons.save.btnText') : $t('buttons.edit.btnText') }}
                </button>
                <div v-else class="btn-group">
                    <button v-if="!editing" type="button" class="btn btn-primary btn" @click.stop="openForEdit">
                        <i class="fas fa-edit"></i>
                        {{ $t('buttons.edit.btnText') }}
                    </button>
                    <button v-if="editing" type="button" class="btn btn-primary btn"
                            :class="saving ? 'disabled' : ''" @click.stop="createOrEdit">
                        <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                        <i class="fas fa-save" v-else></i>
                        {{ $t('buttons.save.btnText') }}
                    </button>
                    <button v-if="editing" type="button" class="btn btn-danger btn"
                            :class="saving ? 'disabled' : ''" @click.stop="cancelEdit">
                        <i class="fas fa-ban"></i>
                        {{ $t('buttons.cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import dropdownSearch from '../dropdown/DropdownSearch';
    import cityDropdownSearch from '../dropdown/CityDropdownSearch';
    import { Address, AreaOfAppeal } from './../../models/models';
    import { Countries, NomenclatureParentBySort } from '../../models/enums';

    export default {
        inject: ['$validator'],
        props: {
            contactAddressId: {
                type: Number,
                default: 0
            },
            address: Address,
            title: String,
            allowEdit: {
                default: false,
                type: Boolean
            },
            loadCurrentUserAddress: {
                default: false,
                type: Boolean
            },
            showAreaOfAppeal: {
                Boolean,
                default: false
            },
            isAddressDetails: {
                type: Boolean,
                default: false
            },
            userId: {
                type: String,
                default: null
            },
            required: {
                type: Boolean,
                default: true
            },
            profileData: {
                type: Object,
                default() {
                    return null;
                }
            }
        },
        components: {
            cityDropdownSearch,
            dropdownSearch
        },
        data() {
            return {
                countries: Countries,
                coordsLoading: false,
                addressLoading: false,
                saving: false,
                editing: false,
                oldValue: new Address(),
                contriesSort: NomenclatureParentBySort.Countries
            };
        },
        created() {
            if (!this.address) {
                this.address = new Address();
            }
        },
        mounted: function () {
            if (this.loadCurrentUserAddress || this.userId) {
                this.getAddress2();
            }
        },
        computed: {
            isEnVersion() {
                return this.$store.getters.currentCulture === 'en';
            },
            areaOfAppeal() {
                if (!this.address.areaOfAppeal) {
                    return '';
                }

                return `${
                    this.isEnVersion
                        ? this.address.areaOfAppeal.nameEn
                        : this.address.areaOfAppeal.name
                } \ ${this.address.areaOfAppeal.districtCode}`;
            },
            isNew() {
                return !this.address || this.address.id <= 0;
            }
        },
        methods: {
            createOrEdit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.saving = true;
                        if (this.isNew) {
                            if (this.userId) {
                                this.address.userId = this.userId;
                            }

                            this.$http
                                .post('/api/Address', this.address)
                                .then(response => {
                                    this.$vs.notify({
                                        title: response.request
                                            ? this.$t(
                                                'request.response.status.' +
                                                response.request.status
                                            )
                                            : this.$t(
                                                'request.response.status.200'
                                            ),
                                        text: response.data.message
                                            ? response.data.message
                                            : response.request.statusText
                                                ? response.request.statusText
                                                : response.message,
                                        color: 'success',
                                        time: this.$store.getters
                                            .notificationDuration
                                    });
                                    this.saving = false;
                                    this.editing = false;
                                })
                                .catch(error => {
                                    this.$vs.notify({
                                        title: this.$t(
                                            'request.response.status.' +
                                            this.$helpers.errorStatusCode(error)
                                        ),
                                        text: this.$helpers.errorText(error),
                                        color: 'danger',
                                        time: this.$store.getters
                                            .notificationDuration
                                    });

                                    this.saving = false;
                                });
                        } else {
                            this.$http
                                .put('/api/Address', this.address)
                                .then(response => {
                                    this.$vs.notify({
                                        title: response.request
                                            ? this.$t(
                                                'request.response.status.' +
                                                response.request.status
                                            )
                                            : this.$t(
                                                'request.response.status.200'
                                            ),
                                        text: response.data.message
                                            ? response.data.message
                                            : response.request.statusText
                                                ? response.request.statusText
                                                : response.message,
                                        color: 'success',
                                        time: this.$store.getters
                                            .notificationDuration
                                    });
                                    this.saving = false;
                                    this.editing = false;
                                })
                                .catch(error => {
                                    this.$vs.notify({
                                        title: this.$t(
                                            'request.response.status.' +
                                            this.$helpers.errorStatusCode(error)
                                        ),
                                        text: this.$helpers.errorText(error),
                                        color: 'danger',
                                        time: this.$store.getters
                                            .notificationDuration
                                    });
                                    this.saving = false;
                                });
                        }
                    }
                });
            },
            openForEdit() {
                this.editing = true;
                this.oldValue = new Address(this.address);
            },
            cancelEdit() {
                this.editing = false;
                this.address = this.oldValue;
            },
            getCurrentUserContactAddress() {
                //     this.$http.get(`/api/Person/AuthenticatedPersonAddress`).then(result => {
                //         this.contactAddressId = result.data.contactAddressId;
                //     }).catch(error => {this.$vs.notify({
                //                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                //                text: this.$helpers.errorText(error),
                //                color: 'danger',
                //                time: this.$store.getters.notificationDuration
                //            });
                //        });
            },
            getAddress2() {
                this.$http
                    .get(`/api/Address/AddressByUserId/${this.userId || ''}`)
                    .then(result => {
                        this.address = new Address(result.data);
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            getAddress() {
                if (!this.address.lat || !this.address.long) {
                    this.$vs.notify({
                        title:
                            'Полето "Ширина" и "Дължина" са без въведена стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.addressLoading = true;

                this.$http
                    .get(
                        '/api/services/getAddressFromCoords/' +
                        `${this.address.lat},${this.address.long}`
                    )
                    .then(response => {
                        this.$vs.notify({
                            title: 'Not implemented',
                            text: '',
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });

                        this.addressLoading = false;
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });

                        this.addressLoading = false;
                    });
            },
            getCoords() {
                if (!this.address.countryId) {
                    this.$vs.notify({
                        title: 'Полето "Държава" е без избрана стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                if (
                    this.address.countryId === this.$store.getters.bulgariaNomId &&
                    !this.address.settlementId
                ) {
                    this.$vs.notify({
                        title: 'Полето "Населено място" е без избрана стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                if (
                    this.address.countryId !== this.$store.getters.bulgariaNomId &&
                    !this.address.settlement
                ) {
                    this.$vs.notify({
                        title: 'Полето "Населено място" е без въвдена стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                if (!this.address.street) {
                    this.$vs.notify({
                        title: 'Полето "Улица" е без въвдена стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                if (!this.address.num) {
                    this.$vs.notify({
                        title: 'Полето "№" е без въвдена стойност!',
                        text: '',
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    });

                    return;
                }

                this.coordsLoading = true;

                this.$http
                    .get(`/api/services/getCoordsFromAddress`, {
                        params: {
                            address: this.fullAddress()
                        }
                    })
                    .then(response => {
                        let searchResult = response.data.searchResult;

                        if (searchResult.position.lat) {
                            this.address.lat = searchResult.position.lat;
                        }

                        if (searchResult.position.lon) {
                            this.address.long = searchResult.position.lon;
                        }

                        this.coordsLoading = false;
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });

                        this.coordsLoading = false;
                    });
            },
            fullAddress() {
                let country = this.$refs.countryDropdown.selected.text;
                let settlement =
                    this.address.countryId === this.$store.getters.bulgariaNomId
                        ? this.$refs.settlementDropdown.selected.text
                        : this.address.settlement;
                let street = this.address.street;
                let streetNum = this.address.num;

                return `${country}, ${settlement}, ${street}, ${streetNum}`;
            },
            cityChanged() {
                if (!this.showAreaOfAppeal) {
                    return;
                }

                this.$http
                    .get(
                        '/api/cities/getAreaOfAppeal/' +
                        `${this.address.settlementId}`
                    )
                    .then(response => {
                        this.address.areaOfAppeal = new AreaOfAppeal(
                            response.data.areaOfAppeal
                        );
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            fillProfileData(data) {
                if (!data) {
                    return;
                }

                console.log(data);
            }
        },
        watch: {
            profileData: {
                handler: function (newVal, oldVal) {
                    this.fillProfileData(newVal);
                },
                deep: true,
                immediate: true,
            },
        }
    };
</script>

<style scoped>
</style>
